import React, {
  useCallback, useMemo, useState, useSyncExternalStore,
} from 'react';
import {
  MdPlayArrow, MdMenuBook, MdPerson, MdPrint, MdFileDownload,
} from 'react-icons/md';
import api from '../../ui/api';
import { isOnline } from '../../ui/cache';
import Modal from '../../ui/Modal';
import PrintDocument from '../../ui/PrintDocument';
import { knowledgeFor, matchGuides } from './match';

/**
 * «اسأل مشكاة» — the conversation, shared by the corner chat
 * (Assistant.js) and the full page «مشكاة الآلي» (pages/MishkahAI.js):
 * one conversation per signed-in user, kept in sessionStorage, the same
 * in both places.
 *
 * A question goes first to /ai/ask (Workers AI picks one read-only query
 * that the server runs with this user's reach — api/src/routes/ai.js):
 * the answer is text + table + chart, or «أيّهم تقصد؟» among same-name
 * pupils (/ai/run, no model call). A «how do I…» question, or anything
 * the data can't answer, goes to the guides (Claude via /assistant/ask
 * when the school set a key, else the guides whose words match).
 */

const KEY = (uid) => `mtz.assistant.${uid}`;
const store = { uid: undefined, snap: { chat: [], busy: false }, subs: new Set() };
const subscribe = (fn) => { store.subs.add(fn); return () => store.subs.delete(fn); };
const read = () => store.snap;
function update(patch) {
  store.snap = { ...store.snap, ...patch };
  if ('chat' in patch) {
    try { sessionStorage.setItem(KEY(store.uid), JSON.stringify(store.snap.chat.slice(-40))); } catch { /* ignore */ }
  }
  store.subs.forEach((fn) => fn());
}
const setChat = (fn) => update({ chat: fn(store.snap.chat) });
function load(uid) {
  if (store.uid === uid) return;
  store.uid = uid;
  let chat = [];
  try { chat = JSON.parse(sessionStorage.getItem(KEY(uid)) || '[]'); } catch { chat = []; }
  store.snap = { chat, busy: false };
}

let seq = 0;
const newId = () => `${Date.now()}-${(seq += 1)}`;

export function useMishkahChat({ user, guides, nav }) {
  load(user?.id);
  const { chat, busy } = useSyncExternalStore(subscribe, read);
  const knowledge = useMemo(() => knowledgeFor(guides, nav), [guides, nav]);
  const byId = useMemo(() => Object.fromEntries(guides.map((x) => [x.id, x])), [guides]);

  const send = useCallback(async (raw) => {
    const question = String(raw || '').trim();
    if (!question || store.snap.busy) return;
    const before = store.snap.chat;
    const local = matchGuides(guides, question).map((x) => x.id);
    const history = before.filter((m) => !m.pending && m.text).slice(-12)
      .map((m) => ({ role: m.from === 'me' ? 'user' : 'assistant', text: m.text }));
    const pendingId = newId();
    update({ busy: true, chat: [...before, { id: newId(), from: 'me', text: question }, { id: pendingId, from: 'bot', pending: true }] });
    const settle = (msg) => setChat((c) => c.map((m) => (m.id === pendingId ? { id: pendingId, from: 'bot', ...msg } : m)));
    const fallback = (lead) => settle({
      text: local.length ? (lead || 'هذه الأدلة الأقرب لسؤالك:') : 'لم أجد جواباً لهذا بعد. جرّب كلمات أخرى، أو تصفّح «دليل الاستخدام».',
      guides: local,
    });
    try {
      if (!isOnline()) { fallback(); return; }
      try {
        // Memory: the last answers, and the pupils they showed («ايمتى سجّل؟» = which one).
        const memory = before.filter((m) => m.from === 'bot' && m.q && !m.pending).slice(-4).map((m) => ({
          q: m.q, tool: m.tool, args: m.args, text: String(m.text || '').slice(0, 220), entities: m.entities,
        }));
        const catalog = guides.map((g) => ({ id: g.id, title: g.title }));
        const d = await api.askData({ question, history: memory, guides: catalog });
        if (d.kind === 'data') { settle({ ...d, q: question }); return; }
        if (d.kind === 'reply' && d.text) { settle({ text: d.text, q: question, tool: 'reply', cost: d.cost }); return; }
        const ids = (d.guides || []).filter((id) => byId[id]);
        if (d.kind === 'howto' && ids.length) { settle({ text: d.text, guides: ids, q: question, tool: 'howto', cost: d.cost }); return; }
      } catch { /* no answer from there: the guides below */ }
      const r = await api.askAssistant({ question, knowledge, history });
      const pointed = (r.guides || []).filter((id) => byId[id]);
      settle({ text: r.answer, guides: pointed, also: pointed.length ? [] : local.slice(0, 2) });
    } catch (err) {
      fallback(err.code === 'AI_LIMIT' ? 'وصلت إلى حدّ الأسئلة لهذا اليوم. هذه الأدلة الأقرب لسؤالك:' : undefined);
    } finally {
      update({ busy: false });
    }
  }, [guides, knowledge, byId]);

  // «أيّهم تقصد؟» — the chosen pupil runs the same query, no model call.
  const choose = useCallback(async (c) => {
    if (store.snap.busy) return;
    const pendingId = newId();
    update({ busy: true, chat: [...store.snap.chat, { id: newId(), from: 'me', text: c.label }, { id: pendingId, from: 'bot', pending: true }] });
    try {
      const d = await api.runData({ tool: c.tool, args: c.args });
      setChat((ch) => ch.map((m) => (m.id === pendingId ? { id: pendingId, from: 'bot', ...d, q: c.label } : m)));
    } catch {
      setChat((ch) => ch.map((m) => (m.id === pendingId ? { id: pendingId, from: 'bot', text: 'تعذّر ذلك الآن، حاول مرة أخرى.' } : m)));
    } finally { update({ busy: false }); }
  }, []);

  const clear = useCallback(() => update({ chat: [] }), []);
  return {
    chat, busy, send, choose, clear, byId,
  };
}

/* ------------------------------------------------------------------ */
/* One message                                                          */
/* ------------------------------------------------------------------ */

/**
 * What a question costs, in the NEW Syrian lira. A fixed rate on purpose —
 * no online lookup: change it here when the rate moves.
 */
export const SYP_PER_USD = 120;
export const costSYP = (usd) => (Number(usd) || 0) * SYP_PER_USD;
export const formatSYP = (syp) => `${syp.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ل.س`;
/** The whole conversation: the questions that went to the AI, and their total. */
export const chatCost = (chat) => {
  const paid = chat.filter((m) => m.cost?.usd);
  return { count: paid.length, syp: paid.reduce((t, m) => t + costSYP(m.cost.usd), 0) };
};

export const TOOL_LABEL = {
  absent_on: 'الغياب في يوم',
  registers_missing: 'شعب بلا تفقّد',
  attendance_ranking: 'أكثر الطلاب غياباً',
  student_day: 'حضور طالب في يوم',
  student_attendance: 'سجل حضور طالب',
  student_marks: 'علامات طالب',
  student_count: 'عدد الطلاب',
  overdue_payments: 'المتأخرون بالدفع',
  student_balance: 'أقساط طالب',
  payments_total: 'مجموع المدفوعات',
  teacher_registers: 'تفقّد المعلمين',
  low_marks: 'العلامات المنخفضة',
  registers_taken: 'الشعب التي أُخذ تفقّدها',
  student_info: 'معلومات طالب',
};

const MAX_ROWS = 80;

function csvOf(table) {
  const cell = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [table.columns.map(([, l]) => cell(l)).join(','), ...table.rows.map((r) => table.columns.map(([k]) => cell(r[k])).join(','))];
  return `﻿${lines.join('\r\n')}`;
}

function DataTable({ table, onStudent, wide }) {
  const rows = table.rows.slice(0, wide ? 500 : MAX_ROWS);
  return (
    <div className={`mk-table-wrap${wide ? ' is-wide' : ''}`}>
      <table className="mk-table">
        <thead><tr>{table.columns.map(([key, label]) => <th key={key}>{label}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              className={r.student_id ? 'is-link' : undefined}
              onClick={r.student_id ? () => onStudent(r.student_id) : undefined}
            >
              {table.columns.map(([key]) => <td key={key}>{r[key] ?? '—'}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      {table.rows.length > rows.length && <div className="mk-table-more">وأكثر: {table.rows.length - rows.length}</div>}
    </div>
  );
}

function Bars({ chart }) {
  const max = Math.max(...chart.items.map((x) => Number(x.value) || 0), 1);
  return (
    <div className="mk-bars" aria-label={chart.label}>
      <div className="mk-bars-title">{chart.label}</div>
      {chart.items.map((x) => (
        <div key={x.label} className="mk-bar">
          <span className="mk-bar-label" title={x.label}>{x.label}</span>
          <span className="mk-bar-track"><span style={{ width: `${Math.max(4, (x.value / max) * 100)}%` }} /></span>
          <b>{Number(x.value).toLocaleString('en-US')}</b>
        </div>
      ))}
    </div>
  );
}

/** A4 print of an answer's table (browser print → paper or PDF). */
function PrintAnswer({ m, onClose }) {
  return (
    <Modal open size="lg" title="طباعة" onClose={onClose}>
      <PrintDocument title={TOOL_LABEL[m.tool] || 'مشكاة الآلي'} subtitle={m.q} onClose={onClose}>
        {m.text && <p className="mk-print-text">{m.text}</p>}
        <table className="mk-print-table">
          <thead><tr><th>#</th>{m.table.columns.map(([k, l]) => <th key={k}>{l}</th>)}</tr></thead>
          <tbody>
            {m.table.rows.map((r, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={i}><td>{i + 1}</td>{m.table.columns.map(([k]) => <td key={k}>{r[k] ?? '—'}</td>)}</tr>
            ))}
          </tbody>
        </table>
      </PrintDocument>
    </Modal>
  );
}

export function ChatMessage({
  m, byId, busy, wide, onRun, onRead, onStudent, onChoose,
}) {
  const [printing, setPrinting] = useState(false);
  if (m.pending) {
    return <div className="as-msg is-bot"><span className="as-typing" aria-label="يكتب"><i /><i /><i /></span></div>;
  }
  const download = () => {
    const url = URL.createObjectURL(new Blob([csvOf(m.table)], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${TOOL_LABEL[m.tool] || 'مشكاة'}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const Guide = ({ id }) => {
    const x = byId[id];
    if (!x) return null;
    return (
      <div className="as-guide">
        <span className={`help-icon help-tone-${x.tone}`}><x.icon size={17} /></span>
        <span className="as-guide-title">{x.title}</span>
        <button type="button" className="as-guide-run" onClick={() => onRun(id)} data-guide={`as-run-${id}`}>
          <MdPlayArrow size={17} /> ورّيني
        </button>
        <button type="button" className="as-guide-read" onClick={() => onRead(id)} title="الخطوات مكتوبة">
          <MdMenuBook size={16} />
        </button>
      </div>
    );
  };
  const hasData = m.table?.rows?.length > 0 || m.chart?.items?.length > 1;
  return (
    <div className={`as-msg ${m.from === 'me' ? 'is-me' : 'is-bot'}${hasData ? ' is-data' : ''}`}>
      {m.tool && TOOL_LABEL[m.tool] && <span className="mk-chip">{TOOL_LABEL[m.tool]}</span>}
      {m.cost?.usd > 0 && (
        <span className="mk-cost" title={`${m.cost.tokens_in + m.cost.tokens_out} توكن · $${m.cost.usd.toFixed(5)}`}>
          كلفة هذا الجواب {formatSYP(costSYP(m.cost.usd))}
        </span>
      )}
      {String(m.text || '').split('\n').filter((l) => l.trim()).map((line, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <p key={i} className={/^\s*\d+[.)-]/.test(line) ? 'is-step' : undefined}>{line}</p>
      ))}
      {m.chart?.items?.length > 1 && <Bars chart={m.chart} />}
      {m.table?.rows?.length > 0 && <DataTable table={m.table} onStudent={onStudent} wide={wide} />}
      {m.table?.rows?.length > 0 && (
        <div className="mk-actions">
          <button type="button" onClick={() => setPrinting(true)}><MdPrint size={16} /> طباعة</button>
          <button type="button" onClick={download}><MdFileDownload size={16} /> Excel</button>
        </div>
      )}
      {m.choices?.length > 0 && (
        <div className="as-choices">
          {m.choices.map((c) => <button key={c.label} type="button" onClick={() => onChoose(c)} disabled={busy}>{c.label}</button>)}
        </div>
      )}
      {m.student_id && (
        <button type="button" className="as-open" onClick={() => onStudent(m.student_id)}><MdPerson size={16} /> ملف الطالب</button>
      )}
      {m.guides?.length > 0 && <div className="as-guides">{m.guides.map((id) => <Guide key={id} id={id} />)}</div>}
      {m.also?.length > 0 && (
        <div className="as-guides"><span className="as-also">قد يفيدك أيضاً</span>{m.also.map((id) => <Guide key={id} id={id} />)}</div>
      )}
      {printing && <PrintAnswer m={m} onClose={() => setPrinting(false)} />}
    </div>
  );
}
